// src/pages/DataExplorer.tsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Download, X, ChevronLeft, ChevronRight, Table2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { API_BASE_URL, apiFetch, getAuthHeaders } from "@/config/api";

type FieldInfo = { name: string; type: string };
type FilterOp = "=" | "!=" | ">" | ">=" | "<" | "<=" | "contains" | "between";
type FilterRow = { field: string; op: FilterOp; value: string; value2?: string };
type CellValue = string | number | boolean | null;

function errMsg(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback;
}

const PAGE_SIZE = 100;
const EXPORT_WARN_THRESHOLD = 2_000_000;
const GAUGE_MAX = 2_500_000;
const THRESH_SAFE = 100_000;
const THRESH_OK = 1_000_000;
const EXPORT_ROWS_PER_SEC_4COLS = 47_500; // cf. README du prototype: ~45-50k lignes/s pour 4 colonnes

function gaugePct(n: number) {
  const clamped = Math.min(Math.max(n, 0), GAUGE_MAX);
  return Math.min(100, Math.max(0, (Math.sqrt(clamped) / Math.sqrt(GAUGE_MAX)) * 100));
}
const Z1 = gaugePct(THRESH_SAFE);
const Z2 = gaugePct(THRESH_OK);
const Z3 = gaugePct(EXPORT_WARN_THRESHOLD);

function buildFilterPayload(filters: FilterRow[]) {
  const asValue = (v: string, op: FilterOp) => {
    if (op === "contains") return v;
    return v !== "" && !isNaN(Number(v)) ? Number(v) : v;
  };
  return filters
    .filter((f) => f.value !== "" && f.value !== undefined)
    .map((f) =>
      f.op === "between"
        ? { field: f.field, op: f.op, value: asValue(f.value, f.op), value2: asValue(f.value2 ?? "", f.op) }
        : { field: f.field, op: f.op, value: asValue(f.value, f.op) }
    );
}

export default function DataExplorer() {
  const { toast } = useToast();

  const [fields, setFields] = useState<FieldInfo[]>([]);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [filters, setFilters] = useState<FilterRow[]>([]);
  const [newFilterField, setNewFilterField] = useState<string>("");

  const [rows, setRows] = useState<CellValue[][]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [loadingFields, setLoadingFields] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [previewTotal, setPreviewTotal] = useState(0);
  const [previewShown, setPreviewShown] = useState(0);
  const [previewMs, setPreviewMs] = useState(0);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const animRef = useRef<number | null>(null);
  const previewShownRef = useRef(0);

  const activeFilters = useMemo(() => filters.filter((f) => f.value !== ""), [filters]);
  const filtersKey = useMemo(() => JSON.stringify(buildFilterPayload(filters)), [filters]);

  const animateCount = useCallback((target: number) => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    const start = previewShownRef.current;
    const t0 = performance.now();
    const duration = 500;
    const step = (now: number) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const value = Math.round(start + (target - start) * eased);
      previewShownRef.current = value;
      setPreviewShown(value);
      if (p < 1) animRef.current = requestAnimationFrame(step);
    };
    animRef.current = requestAnimationFrame(step);
  }, []);

  // -- Chargement initial des champs --
  useEffect(() => {
    (async () => {
      try {
        setLoadingFields(true);
        const data = await apiFetch<{ fields: FieldInfo[]; row_count_hint: number }>(
          "/data-explorer/fields"
        );
        setFields(data.fields);
        setSelectedFields(data.fields.slice(0, 6).map((f) => f.name));
      } catch (e: unknown) {
        toast({
          title: "Erreur",
          description: errMsg(e, "Impossible de charger les champs."),
          variant: "destructive",
        });
      } finally {
        setLoadingFields(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -- Aperçu dynamique du nombre de lignes (avant export), en direct sur les filtres --
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await fetch(`${API_BASE_URL}/data-explorer/count`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({ filters: JSON.parse(filtersKey) }),
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Erreur ${res.status}`);
        const data = await res.json();
        setPreviewTotal(data.total);
        setPreviewMs(data.timing_ms);
        animateCount(data.total);
      } catch (e: unknown) {
        if (!(e instanceof DOMException && e.name === "AbortError")) {
          toast({ title: "Erreur", description: errMsg(e, "Aperçu indisponible."), variant: "destructive" });
        }
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  const runQuery = useCallback(
    async (targetPage: number) => {
      setLoadingGrid(true);
      try {
        const data = await apiFetch<{ columns: string[]; rows: CellValue[][]; total: number }>(
          "/data-explorer/query",
          {
            method: "POST",
            body: JSON.stringify({
              columns: selectedFields,
              filters: JSON.parse(filtersKey),
              page: targetPage,
              page_size: PAGE_SIZE,
            }),
          }
        );
        setColumns(data.columns);
        setRows(data.rows);
        setTotal(data.total);
        setPreviewTotal(data.total);
        animateCount(data.total);
        setPage(targetPage);
      } catch (e: unknown) {
        toast({ title: "Erreur", description: errMsg(e, "Requête impossible."), variant: "destructive" });
      } finally {
        setLoadingGrid(false);
      }
    },
    [selectedFields, filtersKey, toast, animateCount]
  );

  // -- Première requête une fois les champs chargés --
  useEffect(() => {
    if (!loadingFields && selectedFields.length > 0) {
      void runQuery(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingFields]);

  const toggleField = (name: string) => {
    setSelectedFields((prev) => (prev.includes(name) ? prev.filter((f) => f !== name) : [...prev, name]));
  };

  const addFilter = () => {
    if (!newFilterField || filters.some((f) => f.field === newFilterField)) return;
    setFilters((prev) => [...prev, { field: newFilterField, op: "=", value: "" }]);
    setNewFilterField("");
  };

  const removeFilter = (field: string) => setFilters((prev) => prev.filter((f) => f.field !== field));

  const updateFilter = (field: string, patch: Partial<FilterRow>) =>
    setFilters((prev) => prev.map((f) => (f.field === field ? { ...f, ...patch } : f)));

  const numCols = Math.max(1, selectedFields.length);
  const ratePerSec = EXPORT_ROWS_PER_SEC_4COLS * (4 / numCols); // plus de colonnes = export plus lent
  const sheets = Math.max(1, Math.ceil(previewTotal / 1_000_000));
  const estSeconds = previewTotal / ratePerSec;
  const estLabel =
    estSeconds < 1 ? "< 1 s" : estSeconds < 60 ? `~${Math.round(estSeconds)} s` : `~${Math.round(estSeconds / 60)} min`;

  let zone: "safe" | "ok" | "heavy" | "danger" = "safe";
  if (previewTotal > EXPORT_WARN_THRESHOLD) zone = "danger";
  else if (previewTotal > THRESH_OK) zone = "heavy";
  else if (previewTotal > THRESH_SAFE) zone = "ok";

  const zoneMeta = {
    safe: { emoji: "🟢", label: "Léger", border: "border-emerald-500", glow: "" },
    ok: { emoji: "🟡", label: "Confortable", border: "border-amber-500", glow: "" },
    heavy: { emoji: "🟠", label: "Volumineux", border: "border-orange-500", glow: "" },
    danger: {
      emoji: "🔴",
      label: "Très volumineux",
      border: "border-red-500",
      glow: "animate-pulse shadow-[0_0_18px_rgba(239,68,68,0.45)]",
    },
  }[zone];

  const handleExport = async () => {
    if (previewTotal > EXPORT_WARN_THRESHOLD) {
      const ok = window.confirm(
        `${previewTotal.toLocaleString("fr-FR")} lignes correspondent aux filtres actuels.\n\n` +
          `Générer un fichier Excel à cette échelle peut prendre plusieurs minutes.\n\n` +
          `Astuce : affinez vos filtres pour réduire le résultat, ou continuez si vous acceptez le temps de génération.\n\n` +
          `Continuer l'export ?`
      );
      if (!ok) return;
    }
    setExporting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/data-explorer/export`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ columns: selectedFields, filters: JSON.parse(filtersKey) }),
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || `Export impossible (${res.status})`);
      }
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="(.+)"/);
      const filename = match ? match[1] : "export.xlsx";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast({ title: "Export terminé", description: `Le fichier ${filename} a été téléchargé.` });
    } catch (e: unknown) {
      toast({ title: "Erreur d'export", description: errMsg(e, "Export impossible."), variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Table2 className="h-5 w-5" />
          Data Explorer — Grand Livre
        </h1>
        <p className="text-sm text-muted-foreground">
          Filtres dynamiques et export Excel sur les écritures comptables.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        {/* Colonne gauche: champs + filtres */}
        <div className="space-y-4">
          <Card className="p-4">
            <h2 className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Colonnes</h2>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {fields.map((f) => (
                <label key={f.name} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={selectedFields.includes(f.name)} onCheckedChange={() => toggleField(f.name)} />
                  <span className="truncate">{f.name}</span>
                </label>
              ))}
              {loadingFields && <p className="text-xs text-muted-foreground">Chargement…</p>}
            </div>
          </Card>

          <Card className="p-4">
            <h2 className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Filtres</h2>
            <div className="flex gap-2 mb-3">
              <Select value={newFilterField} onValueChange={setNewFilterField}>
                <SelectTrigger className="text-xs h-8">
                  <SelectValue placeholder="Ajouter un filtre…" />
                </SelectTrigger>
                <SelectContent>
                  {fields
                    .filter((f) => !filters.some((flt) => flt.field === f.name))
                    .map((f) => (
                      <SelectItem key={f.name} value={f.name}>
                        {f.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="secondary" onClick={addFilter} disabled={!newFilterField}>
                +
              </Button>
            </div>
            <div className="space-y-3">
              {filters.map((f) => (
                <div key={f.field} className="border rounded-md p-2 space-y-2 bg-muted/30">
                  <div className="flex items-center justify-between text-xs font-medium">
                    <span className="truncate">{f.field}</span>
                    <button onClick={() => removeFilter(f.field)} className="text-destructive hover:opacity-70">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <Select value={f.op} onValueChange={(v) => updateFilter(f.field, { op: v as FilterOp })}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="=">=</SelectItem>
                      <SelectItem value="!=">≠</SelectItem>
                      <SelectItem value=">">&gt;</SelectItem>
                      <SelectItem value=">=">&ge;</SelectItem>
                      <SelectItem value="<">&lt;</SelectItem>
                      <SelectItem value="<=">&le;</SelectItem>
                      <SelectItem value="contains">contient</SelectItem>
                      <SelectItem value="between">entre</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    className="h-8 text-xs"
                    placeholder="valeur"
                    value={f.value}
                    onChange={(e) => updateFilter(f.field, { value: e.target.value })}
                  />
                  {f.op === "between" && (
                    <Input
                      className="h-8 text-xs"
                      placeholder="valeur (fin)"
                      value={f.value2 ?? ""}
                      onChange={(e) => updateFilter(f.field, { value2: e.target.value })}
                    />
                  )}
                </div>
              ))}
              {filters.length === 0 && <p className="text-xs text-muted-foreground">Aucun filtre actif.</p>}
            </div>
          </Card>
        </div>

        {/* Colonne droite: aperçu + grille */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Button onClick={() => runQuery(1)} disabled={loadingGrid}>
              Appliquer les filtres
            </Button>
            <Button variant="secondary" onClick={handleExport} disabled={exporting || previewTotal === 0}>
              <Download className="h-4 w-4 mr-1.5" />
              {exporting ? "Génération…" : "Exporter en Excel (.xlsx)"}
            </Button>
          </div>

          <Card
            className={`p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4 border-2 transition-colors ${zoneMeta.border} ${zoneMeta.glow}`}
          >
            <div className="flex items-center gap-3 min-w-[190px]">
              <span className="text-2xl">{zoneMeta.emoji}</span>
              <div>
                <div className="text-xl font-bold tabular-nums leading-tight">
                  {previewShown.toLocaleString("fr-FR")}
                </div>
                <div className="text-xs text-muted-foreground">lignes correspondent au filtre actuel</div>
              </div>
            </div>
            <div className="flex-1 min-w-[180px] w-full">
              <div
                className="relative h-2 rounded-full"
                style={{
                  background: `linear-gradient(to right, #22c55e 0%, #22c55e ${Z1}%, #eab308 ${Z1}%, #eab308 ${Z2}%, #f97316 ${Z2}%, #f97316 ${Z3}%, #ef4444 ${Z3}%, #ef4444 100%)`,
                }}
              >
                <div
                  className="absolute -top-1 w-[3px] h-4 bg-white rounded-sm shadow transition-all duration-500"
                  style={{ left: `${gaugePct(previewTotal)}%`, transform: "translateX(-50%)" }}
                />
              </div>
              <div className="relative h-3 mt-1 text-[10px] text-muted-foreground">
                <span className="absolute" style={{ left: "0%" }}>
                  0
                </span>
                <span className="absolute" style={{ left: `${Z1}%`, transform: "translateX(-50%)" }}>
                  100K
                </span>
                <span className="absolute" style={{ left: `${Z2}%`, transform: "translateX(-50%)" }}>
                  1M/feuille
                </span>
                <span className="absolute" style={{ left: `${Z3}%`, transform: "translateX(-100%)" }}>
                  2M+ (alerte)
                </span>
              </div>
            </div>
            <div className="text-xs text-muted-foreground text-right min-w-[190px]">
              <div>
                <b className="text-foreground">{zoneMeta.label}</b> — {sheets} feuille{sheets > 1 ? "s" : ""} (
                {numCols} colonne{numCols > 1 ? "s" : ""})
              </div>
              <div>
                export estimé: <b className="text-foreground">{estLabel}</b> · compté en {previewMs} ms
              </div>
            </div>
          </Card>

          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((c) => (
                    <TableHead key={c}>{c}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i}>
                    {r.map((v, j) => (
                      <TableCell key={j} className="whitespace-nowrap text-sm">
                        {v === null || v === undefined ? "" : String(v)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {rows.length === 0 && !loadingGrid && (
                  <TableRow>
                    <TableCell colSpan={Math.max(columns.length, 1)} className="text-center text-muted-foreground py-8">
                      Aucune donnée.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {total.toLocaleString("fr-FR")} lignes au total — page {page} / {totalPages}
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1 || loadingGrid} onClick={() => runQuery(page - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages || loadingGrid}
                onClick={() => runQuery(page + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
